import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { Box, Typography } from '@mui/material';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import MapGl, { Layer, Source } from 'react-map-gl';
import { isConfirmedNonUsCountry, isPlaceholderDimension, num } from '../format';
import { type CityFeature, useCityBoundaries } from '../hooks/useCityBoundaries';
import { COLORS } from '../theme';
import MapHoverTooltip from './MapHoverTooltip';
import MapLegend from './MapLegend';
import WidgetSkeleton from './WidgetSkeleton';
import { buildFillPaint } from './geoFillLayer';
import {
  type FocusRequest,
  computeAutoFocusViewState,
  computeClickFocusViewState,
  createClickFocusTracker,
  createFocusTracker,
} from './mapViewport';

const VITE_MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const MAX_CITY_CODES = 100;
// Same default camera as the legacy Mapbox maps (constants/geo.js
// INITIAL_VIEW_STATE), minus its width/height fields — sizing here comes
// from the container Box, and react-map-gl's ViewState type doesn't have them.
const INITIAL_VIEW_STATE = { latitude: 38, longitude: -97, zoom: 3, bearing: 0, pitch: 0 };

/**
 * City map — a real Mapbox GL choropleth. See DmaChoropleth for the full
 * rationale (chartjs-plugin-zoom doesn't support chartjs-chart-geo's
 * projection scale; Mapbox has zoom/pan/pinch built in). Cities number in
 * the tens of thousands — the backend's plain `/geo/boundaries` endpoint
 * refuses an unbounded City fetch, so useCityBoundaries asks only for the
 * cities present in the widget's own rows, via the joint-boundaries
 * endpoint. Capped at 100 per request, matching the existing Simplifi
 * city-boundary fetch's own limit. A caption below the map discloses the
 * cap once it actually bites (review, Lucas), same as ZipChoropleth.
 */
interface IProps {
  rows: ReportRow[];
  /** The widget's true distinct-row count, independent of the page `rows` holds — lets the cap disclosure below say how many cities are actually missing instead of just that some are (review, Lucas, matching ZipChoropleth). */
  totalRows?: number;
  labelKey?: string;
  valueKey?: string;
  stateKey?: string;
  /**
   * flank#4813 — a non-US city lookup used slots the same way a non-US ZIP
   * did. Requesting this alongside `labelKey`/`stateKey` lets a
   * positively-identified non-US row get dropped before it reaches boundary
   * matching below. Optional: a caller that doesn't fetch `geo_country` just
   * never has anything to filter, same as before this fix.
   */
  countryKey?: string;
  metricLabel?: string;
  formatValue?: (v: number) => string;
  /** flank#4755 — a row clicked in the ranked list below, keyed
   * "`${name}, ${state}`" (same shape as `valueByKey`); the map zooms to it.
   * Null/cleared is a no-op, not a reset to default. `requestId` changes on
   * every click (even a repeat click on the same row) so a dataset change
   * can't replay a stale selection over the new auto-focus (review,
   * CodeRabbit on #4777). */
  focusRequest?: FocusRequest | null;
}

const CityChoropleth = ({
  rows,
  totalRows = 0,
  labelKey = 'geo_city',
  valueKey = 'impressions',
  stateKey = 'geo_state_code',
  countryKey = 'geo_country',
  metricLabel = 'Impressions',
  formatValue = num,
  focusRequest = null,
}: IProps) => {
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const [hover, setHover] = useState<{ x: number; y: number; label: string; value: number } | null>(null);
  // flank#4748 — "follow legacy" AND re-centre on every filter change (not
  // just initial mount): tracks the last `features` array auto-focused for,
  // so it fires once per genuinely new dataset (rows and/or boundaries
  // actually changed) but never fights a pan/zoom-only re-render, since
  // `features` below is memoized and so stays reference-equal across those.
  const focusTrackerRef = useRef(createFocusTracker<CityFeature>());
  const clickFocusTrackerRef = useRef(createClickFocusTracker());

  // isPlaceholderDimension, not just the Unmapped check (review, Lucas): DV360's
  // literal "Unknown" has a real name/state-shaped value and would otherwise pass
  // the Boolean filter below, spending one of the 100 lookup slots on a city that
  // was never going to resolve to real boundaries. isConfirmedNonUsCountry
  // (flank#4813) drops a row whose country is a positively-identified non-US
  // value for the same reason — a non-US city lookup used a slot the same way.
  const realRows = useMemo(
    () => rows.filter(row => !isPlaceholderDimension(row[labelKey]) && !isConfirmedNonUsCountry(row[countryKey])),
    [rows, labelKey, countryKey],
  );
  // Distinct, present name+state pairs only — a row missing either can't be
  // resolved, and re-requesting the same set every render would refetch on
  // every unrelated re-render, so this is keyed on the joined lookup list.
  const allLookups = Array.from(
    new Set(
      realRows
        .map(row => {
          const name = String(row[labelKey] ?? '').trim();
          const state = String(row[stateKey] ?? '').trim();
          return name && state ? `${name}|${state}` : '';
        })
        .filter(Boolean),
    ),
  );
  const lookups = allLookups.slice(0, MAX_CITY_CODES);
  const { features: boundaries, failed } = useCityBoundaries(lookups);

  // Memoized on the actual data inputs (not recomputed fresh every render)
  // so its reference only changes when rows or boundaries genuinely change —
  // the signal computeAutoFocusViewState below uses to tell a filter-driven
  // refresh apart from an unrelated re-render (pan, hover).
  const valueByKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of realRows) {
      const name = String(row[labelKey] ?? '').trim();
      const state = String(row[stateKey] ?? '').trim();
      const value = Number(row[valueKey] ?? 0);
      if (!name || !state || !Number.isFinite(value) || value <= 0) continue;
      const key = `${name}, ${state}`;
      // Sum, don't overwrite (CodeRabbit, flank#4813): a US row and a
      // missing-country row for the same city both pass isConfirmedNonUsCountry
      // (it only drops a positively-identified non-US value), so they can share
      // this key — `set` would silently drop one row's volume off the map.
      map.set(key, (map.get(key) ?? 0) + value);
    }
    return map;
  }, [realRows, labelKey, stateKey, valueKey]);
  const features = useMemo<CityFeature[]>(() => {
    if (!boundaries) return [];
    return boundaries.map(f => {
      const key = String(f.properties?.GEO_ID ?? '').trim();
      return { ...f, properties: { ...f.properties, value: valueByKey.get(key) ?? null } };
    });
  }, [boundaries, valueByKey]);
  const scaleMax = useMemo(() => Math.max(0, ...Array.from(valueByKey.values())), [valueByKey]);

  // flank#4748: fires only when `features` is a new reference (rows and/or
  // boundaries actually changed, e.g. a filter change landed new data) —
  // skipped on a re-render carrying the SAME features (pan, hover), so the
  // user's own interaction is never fought. In a useEffect (code review,
  // #0000), not the render body: computeAutoFocusViewState mutates the
  // tracker ref as a side effect, and running that during the render phase
  // meant a discarded render (StrictMode double-invoke, an interrupted
  // concurrent render) could consume the tracker without its setViewState
  // ever committing, silently eating the auto-focus for the "real" render.
  // Declared before the early returns below (Rules of Hooks).
  useEffect(() => {
    const autoFocusedViewState = computeAutoFocusViewState(focusTrackerRef.current, features, INITIAL_VIEW_STATE);
    if (autoFocusedViewState) setViewState(autoFocusedViewState);
  }, [features]);

  // flank#4755 — depends on `features` too (unlike the auto-focus effect
  // above) so a click that lands before the city boundary fetch resolves
  // still focuses once the matching feature actually appears. Matches on
  // GEO_ID the same way `valueByKey` above does, so the key the parent sends
  // (built the same "`${name}, ${state}`" way) lines up with the feature the
  // map actually colored for that row.
  useEffect(() => {
    const clickedViewState = computeClickFocusViewState(clickFocusTrackerRef.current, features, focusRequest, f =>
      String(f.properties?.GEO_ID ?? ''),
    );
    if (clickedViewState) setViewState(clickedViewState);
  }, [focusRequest, features]);

  // Disclosure for the 100-city cap (review, Lucas): a report can return tens
  // of thousands of distinct cities, and this silently rendered only the
  // first 100 with nothing on screen saying so. Same shape as ZipChoropleth's
  // cap caption — `totalRows` is the TRUE distinct-row count, not just this
  // page's, and `lookups.length` (not MAX_CITY_CODES) in case fewer than the
  // cap turned up resolvable name+state pairs.
  //
  // Approximate, not exact (review, CodeRabbit): the query sorts by impressions
  // and caps at 300 rows, so a placeholder row that ranks below the cutoff never
  // reaches `rows` at all — the subtraction below only catches placeholders that
  // ARE on this page, and can overstate the real city count by however many
  // placeholder rows got cut off entirely.
  const totalRealCities = Math.max(0, totalRows - (rows.length - realRows.length));
  const capped = totalRealCities > MAX_CITY_CODES;

  if (failed) {
    return (
      <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
        Couldn't load the city boundaries. The ranked list below still reflects the data.
      </Typography>
    );
  }
  if (!boundaries) return <WidgetSkeleton height={360} />;

  const geojson = { type: 'FeatureCollection' as const, features };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ height: 360, width: '100%', position: 'relative' }}>
        <MapGl
          {...viewState}
          style={{ width: '100%', height: '100%' }}
          onMove={e => setViewState(e.viewState)}
          mapStyle='mapbox://styles/mapbox/light-v9'
          mapboxAccessToken={VITE_MAPBOX_TOKEN}
          interactiveLayerIds={['data']}
          onMouseMove={e => {
            const feature = e.features?.[0] as CityFeature | undefined;
            const value = feature?.properties?.value;
            if (!feature || value == null) {
              setHover(null);
              return;
            }
            setHover({
              x: e.point.x,
              y: e.point.y,
              label: feature.properties?.NAME ?? feature.properties?.name ?? '',
              value,
            });
          }}
          onMouseLeave={() => setHover(null)}
        >
          <Source type='geojson' data={geojson}>
            <Layer id='data' type='fill' paint={buildFillPaint(features) as any} />
          </Source>
        </MapGl>
        {hover && (
          <MapHoverTooltip
            x={hover.x}
            y={hover.y}
            label={hover.label}
            metricLabel={metricLabel}
            value={formatValue(hover.value)}
          />
        )}
      </Box>
      <MapLegend max={scaleMax} />
      {capped && (
        <Typography variant='body2' sx={{ color: COLORS.muted, fontSize: 13 }}>
          {`Map shows the top ${lookups.length.toLocaleString()} of approximately ${totalRealCities.toLocaleString()} cities.`}
        </Typography>
      )}
    </Box>
  );
};

export default CityChoropleth;
